<template>
  <div class="min-h-screen bg-white dark:bg-zinc-950">
    <!-- Hero Section -->
    <div
      class="relative px-4 py-20 overflow-hidden bg-gradient-to-br from-white via-gray-100 to-gray-200 dark:from-gray-900 dark:via-zinc-900 dark:to-black"
    >
      <!-- Decorative elements -->
      <div class="absolute inset-0 opacity-30 dark:opacity-20">
        <div
          class="absolute rounded-full top-10 left-10 w-72 h-72 bg-mainYellow/20 dark:bg-mainYellow/10 blur-3xl"
        ></div>
        <div
          class="absolute bottom-0 rounded-full right-20 w-96 h-96 bg-mainYellow/10 dark:bg-mainYellow/5 blur-3xl"
        ></div>
      </div>

      <div class="relative mx-auto text-center max-w-7xl">
        <div class="inline-block mb-6">
          <span
            class="px-4 py-2 text-sm font-semibold border rounded-full text-mainYellow bg-mainYellow/10 border-mainYellow/30"
          >
            {{ $t("school.hero.badge") }}
          </span>
        </div>
        <h1
          class="mb-6 text-6xl font-bold text-gray-900 dark:text-white lg:text-7xl"
        >
          {{ $t("school.hero.title") }}
          <span class="text-mainYellow">{{
            $t("school.hero.titleHighlight")
          }}</span>
        </h1>
        <p
          class="max-w-3xl mx-auto mb-8 text-xl leading-relaxed text-gray-700 dark:text-gray-300 lg:text-2xl"
        >
          {{ $t("school.hero.subtitle") }}
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="px-4 py-20">
      <div class="mx-auto max-w-7xl">
        <!-- Stats Section -->
        <div class="grid grid-cols-1 gap-8 mb-20 md:grid-cols-4">
          <div class="text-center">
            <div class="mb-2 text-4xl font-bold text-mainYellow">
              {{ $t("school.stats.experienceValue") }}
            </div>
            <p class="text-gray-600 dark:text-zinc-400">
              {{ $t("school.stats.experience") }}
            </p>
          </div>
          <div class="text-center">
            <div class="mb-2 text-4xl font-bold text-mainYellow">
              {{ $t("school.stats.studentsValue") }}
            </div>
            <p class="text-gray-600 dark:text-zinc-400">
              {{ $t("school.stats.students") }}
            </p>
          </div>
          <div class="text-center">
            <div class="mb-2 text-4xl font-bold text-mainYellow">
              {{ $t("school.stats.equipmentValue") }}
            </div>
            <p class="text-gray-600 dark:text-zinc-400">
              {{ $t("school.stats.equipment") }}
            </p>
          </div>
          <div class="text-center">
            <div class="mb-2 text-4xl font-bold text-mainYellow">
              {{ $t("school.stats.supportValue") }}
            </div>
            <p class="text-gray-600 dark:text-zinc-400">
              {{ $t("school.stats.support") }}
            </p>
          </div>
        </div>

        <!-- Courses Section -->
        <div class="mb-20">
          <div class="mb-16 text-center">
            <h2
              class="mb-4 text-4xl font-bold text-gray-900 lg:text-5xl dark:text-zinc-100"
            >
              {{ $t("school.courses.sectionTitle") }}
            </h2>
            <p class="text-lg text-gray-600 dark:text-zinc-400">
              {{ $t("school.courses.sectionSubtitle") }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div
              v-for="(subject, index) in courses"
              :key="index"
              class="relative group"
            >
              <!-- Card background glow on hover -->
              <div
                class="absolute -inset-0.5 bg-gradient-to-r from-mainYellow to-mainYellow/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur group-hover:blur-md"
              ></div>

              <!-- Main Card -->
              <div
                class="relative flex flex-col h-full overflow-hidden transition-all duration-300 bg-white border border-gray-200 shadow-lg dark:bg-zinc-900 rounded-2xl group-hover:shadow-2xl dark:border-zinc-800"
              >
                <!-- Icon & Background -->
                <div
                  class="relative flex items-center justify-center h-40 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-zinc-800 dark:to-zinc-900 group-hover:scale-105"
                >
                  <div class="absolute inset-0 opacity-10">
                    <div
                      class="absolute top-0 right-0 w-40 h-40 -mt-20 -mr-20 rounded-full bg-mainYellow"
                    ></div>
                  </div>
                  <div
                    class="text-6xl transition-transform duration-300 group-hover:scale-110"
                  >
                    {{ courseIcons[index] }}
                  </div>
                </div>

                <!-- Content -->
                <div class="flex flex-col flex-grow p-8">
                  <h3
                    class="mb-3 text-2xl font-bold text-gray-900 dark:text-zinc-100"
                  >
                    {{ $t(`school.classes.subjects.${subject.key}.title`) }}
                  </h3>

                  <p
                    class="flex-grow mb-6 leading-relaxed text-gray-600 dark:text-zinc-300"
                  >
                    {{ $t(`school.classes.subjects.${subject.key}.content`) }}
                  </p>

                  <!-- Features list -->
                  <div class="mb-6 space-y-2">
                    <div
                      class="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300"
                    >
                      <span class="text-mainYellow">✓</span>
                      <span>{{ $t("school.courses.privateOrGroup") }}</span>
                    </div>
                    <div
                      class="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300"
                    >
                      <span class="text-mainYellow">✓</span>
                      <span>{{
                        $t("school.courses.professionalEquipment")
                      }}</span>
                    </div>
                    <div
                      class="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300"
                    >
                      <span class="text-mainYellow">✓</span>
                      <span>{{ $t("school.courses.flexibleSchedules") }}</span>
                    </div>
                  </div>

                  <!-- Price & CTA -->
                  <div
                    class="pt-6 border-t border-gray-200 dark:border-zinc-700"
                  >
                    <div
                      v-if="getPrice('classes', subject.key)"
                      class="flex items-baseline gap-2 mb-4"
                    >
                      <span class="text-4xl font-bold text-mainYellow">
                        {{ getPrice("classes", subject.key) }}
                      </span>
                      <span class="text-gray-600 dark:text-zinc-400">
                        {{
                          $t(
                            `school.classes.subjects.${subject.key}.postPriceMessage`
                          )
                        }}
                      </span>
                    </div>
                    <button
                      class="w-full py-3 font-semibold text-gray-900 transition-colors rounded-lg bg-mainYellow hover:bg-mainYellowDarkerr"
                    >
                      {{ $t("school.courses.scheduleAula") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Why Choose Us Section -->
        <div class="grid items-center grid-cols-1 gap-16 mb-20 lg:grid-cols-2">
          <div>
            <h2
              class="mb-8 text-4xl font-bold text-gray-900 dark:text-zinc-100"
            >
              {{ $t("school.whyChoose.title") }}
              <span class="text-mainYellow">{{
                $t("school.whyChoose.titleHighlight")
              }}</span
              >?
            </h2>

            <div class="space-y-6">
              <div class="flex gap-4">
                <div class="flex-shrink-0">
                  <div
                    class="flex items-center justify-center w-12 h-12 font-bold text-gray-900 rounded-lg bg-mainYellow"
                  >
                    1
                  </div>
                </div>
                <div>
                  <h3
                    class="mb-2 text-lg font-semibold text-gray-900 dark:text-zinc-100"
                  >
                    {{ $t("school.whyChoose.item1Title") }}
                  </h3>
                  <p class="text-gray-600 dark:text-zinc-400">
                    {{ $t("school.whyChoose.item1Description") }}
                  </p>
                </div>
              </div>

              <div class="flex gap-4">
                <div class="flex-shrink-0">
                  <div
                    class="flex items-center justify-center w-12 h-12 font-bold text-gray-900 rounded-lg bg-mainYellow"
                  >
                    2
                  </div>
                </div>
                <div>
                  <h3
                    class="mb-2 text-lg font-semibold text-gray-900 dark:text-zinc-100"
                  >
                    {{ $t("school.whyChoose.item2Title") }}
                  </h3>
                  <p class="text-gray-600 dark:text-zinc-400">
                    {{ $t("school.whyChoose.item2Description") }}
                  </p>
                </div>
              </div>

              <div class="flex gap-4">
                <div class="flex-shrink-0">
                  <div
                    class="flex items-center justify-center w-12 h-12 font-bold text-gray-900 rounded-lg bg-mainYellow"
                  >
                    3
                  </div>
                </div>
                <div>
                  <h3
                    class="mb-2 text-lg font-semibold text-gray-900 dark:text-zinc-100"
                  >
                    {{ $t("school.whyChoose.item3Title") }}
                  </h3>
                  <p class="text-gray-600 dark:text-zinc-400">
                    {{ $t("school.whyChoose.item3Description") }}
                  </p>
                </div>
              </div>

              <div class="flex gap-4">
                <div class="flex-shrink-0">
                  <div
                    class="flex items-center justify-center w-12 h-12 font-bold text-gray-900 rounded-lg bg-mainYellow"
                  >
                    4
                  </div>
                </div>
                <div>
                  <h3
                    class="mb-2 text-lg font-semibold text-gray-900 dark:text-zinc-100"
                  >
                    {{ $t("school.whyChoose.item4Title") }}
                  </h3>
                  <p class="text-gray-600 dark:text-zinc-400">
                    {{ $t("school.whyChoose.item4Description") }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="relative hidden lg:block">
            <CustomCarousel :items="items" />
          </div>
        </div>

        <!-- Mobile Carousel for smaller screens -->
        <div class="mb-20 lg:hidden">
          <CustomCarousel :items="items" />
          <img :src="items[0].path" />
        </div>

        <!-- CTA Section -->
        <div
          class="p-12 text-center border border-gray-300 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-gray-900 dark:via-zinc-900 dark:to-black rounded-2xl dark:border-mainYellow/20"
        >
          <h2 class="mb-4 text-4xl font-bold text-gray-900 dark:text-white">
            {{ $t("school.cta.title") }}
          </h2>
          <p
            class="max-w-2xl mx-auto mb-8 text-xl text-gray-700 dark:text-gray-400"
          >
            {{ $t("school.cta.subtitle") }}
          </p>
          <div class="flex flex-col justify-center gap-4 sm:flex-row">
            <ContactButtons />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

const { t } = useI18n();
const { courses, courseIcons } = useSchoolCourses();

// Fetch prices from API
const { data: pricesData } = usePrices();

const getPrice = (sectionKey: string, subjectKey: string) => {
  if (!pricesData.value) return "";

  // A API retorna: pricesData.school.classes.school[key]
  const price =
    (pricesData.value?.school?.classes as any)?.school?.[subjectKey] ?? null;
  return price !== null ? price : t("nullPrice");
};

const items = [
  { path: "/images/media/school/media4.jpg", alt: "Modern studio workspace" },
  { path: "/images/media/school/media9.jpg", alt: "Modern studio workspace 2" },
];

useSeoMeta({
  title: t("menuKeys.school"),
  description:
    "Highfield Music Studio provides classes for instruments, DJing, music production, monthly packs, and group lessons.",
  keywords:
    "studio, music, instruments, recording, audio equipment, midi, dj gear, microphones, studio equipment",
  ogTitle: "School | Highfield Music Studio",
  ogDescription:
    "Highfield Music Studio provides classes for instruments, DJing, music production, monthly packs, and group lessons.",
  ogImage: "/images/logos/studio/1.jpg",
  twitterCard: "summary_large_image",
  twitterTitle: "School | Highfield Music Studio",
  twitterDescription:
    "Highfield Music Studio provides classes for instruments, DJing, music production, monthly packs, and group lessons.",
  twitterImage: "/images/media/studio/1.jpg",
});
</script>
